import {
  ADS_ACTIVITY_DAILY_MAX_PER_PLATFORM_MAX,
  ADS_ACTIVITY_DAILY_MAX_PER_PLATFORM_MIN,
  type AdsAssistantOrgPrefs,
} from "@/lib/adsAssistantOrgSettings";

const HHMM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

/**
 * Org Activity coaching sub-card for the Ads Org Settings panel (trainer+).
 * Phase 1 introduces:
 *   - `red_after_local_time` — when an unmet goal flips amber → red.
 *   - `daily_max_per_platform` — soft cap per platform per day.
 * The derived "Daily soft cap" preview keeps trainers honest about how many
 * ads the cap actually permits with their current platform list.
 */
export function AdsAssistantOrgSettingsActivityCoachingCard(props: {
  prefs: AdsAssistantOrgPrefs;
  setPrefs: (updater: (prev: AdsAssistantOrgPrefs) => AdsAssistantOrgPrefs) => void;
  platformCount: number;
  loading: boolean;
}) {
  const { prefs, setPrefs, platformCount, loading } = props;
  const softCap = prefs.daily_max_per_platform * platformCount;
  const cutoffValid = HHMM_RE.test(prefs.red_after_local_time);

  return (
    <div className="rounded-xl border border-orange-200/80 dark:border-orange-800/50 bg-orange-50/35 dark:bg-orange-950/20 px-4 py-4 space-y-3">
      <div>
        <span className="text-xs font-semibold text-gray-800 dark:text-gray-100">Org Activity coaching</span>
        <p className="mt-1 text-[11px] text-gray-600 dark:text-gray-400 leading-snug">
          Drives the Org Activity gamification verdict (amber → green → gold). Unmet daily goals flip to{" "}
          <strong className="text-red-700 dark:text-red-300">red</strong> after the cutoff. The soft cap rewards
          quality cadence — going past it lands in the grey &ldquo;well past goal&rdquo; footer.
        </p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="text-xs text-gray-700 dark:text-gray-300 block">
          Daily goal cutoff (local time)
          <input
            type="time"
            className="mt-1 w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-zinc-900"
            value={prefs.red_after_local_time}
            onChange={(e) => setPrefs((pr) => ({ ...pr, red_after_local_time: e.target.value }))}
            disabled={loading}
          />
          {cutoffValid ? null : (
            <span className="mt-0.5 block text-[10px] text-red-700 dark:text-red-300">
              Use HH:MM (24-hour); defaults to 15:00.
            </span>
          )}
        </label>
        <label className="text-xs text-gray-700 dark:text-gray-300 block">
          Soft cap per platform
          <input
            type="number"
            min={ADS_ACTIVITY_DAILY_MAX_PER_PLATFORM_MIN}
            max={ADS_ACTIVITY_DAILY_MAX_PER_PLATFORM_MAX}
            className="mt-1 w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-zinc-900"
            value={prefs.daily_max_per_platform}
            onChange={(e) =>
              setPrefs((pr) => ({
                ...pr,
                daily_max_per_platform: Math.min(
                  ADS_ACTIVITY_DAILY_MAX_PER_PLATFORM_MAX,
                  Math.max(ADS_ACTIVITY_DAILY_MAX_PER_PLATFORM_MIN, parseInt(e.target.value, 10) || 1),
                ),
              }))
            }
            disabled={loading}
          />
        </label>
      </div>
      <p className="text-[11px] text-gray-700 dark:text-gray-300">
        Daily soft cap ={" "}
        <span className="font-mono tabular-nums">
          {prefs.daily_max_per_platform} × {platformCount}
        </span>{" "}
        = <span className="font-semibold tabular-nums">{softCap}</span> ads
      </p>
    </div>
  );
}
